import { CHAIN_NAMESPACES, type CustomChainConfig } from "@web3auth/no-modal";

import config from "@/config";

import { ChainsListRegistryChainConfigItem, EIP_STANDARD, EipStandard, SmartAccountChainConfigItem } from "./interface";

// we get chain logo from https://assets.web3auth.io/v1/chains.json
/** e.g. from logo property we get '995.svg'
 *   
 * {
    "chainNamespace": "eip155",
    "chainId": "0x3e3",
    "rpcTarget": "https://rpc.5ire.network",
    "displayName": "5ireChain",
    "blockExplorerUrl": "https://5irescan.io/",
    "ticker": "5IRE",
    "tickerName": "5ire Token",
    "logo": "995.svg",
    "isTestnet": false,
    "isInfuraSupported": false,
    "decimals": 18
  },
 */
export const constructChainLogoUrl = (apiChainConfigItem: ChainsListRegistryChainConfigItem) => {
  return `${config.web3AuthAssetsBaseUrl}/chains/${apiChainConfigItem.logo}`;
};

const SOLANA_CAIP_CHAIN_MAP: Record<string, string> = {
  "0x65": "5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
  "0x66": "4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
  "0x67": "EtWTRABZaYq6iMfeYKouRu166VU2xqa1",
} as const;

export const getCaipChainId = (chain: Pick<CustomChainConfig, "chainNamespace" | "chainId">) => {
  if (chain.chainNamespace === CHAIN_NAMESPACES.EIP155) {
    return `${chain.chainNamespace}:${parseInt(chain.chainId, 16)}`;
  }
  if (chain.chainNamespace === CHAIN_NAMESPACES.SOLANA) {
    return `${chain.chainNamespace}:${SOLANA_CAIP_CHAIN_MAP[chain.chainId]}`;
  }

  // for other chain namespaces, we just return the chainId as is
  return `${chain.chainNamespace}:${chain.chainId}`;
};

type SmartAccountEligibleNetwork = {
  enabled?: boolean;
  chainNamespace?: string;
  isInfuraSupported?: boolean;
};

export const filterEligibleSmartAccountNetworks = <T extends SmartAccountEligibleNetwork>(
  networks: ReadonlyArray<T>,
  eipStandard: EipStandard
): T[] => {
  return networks.filter((network) => {
    if (!network.enabled || network.chainNamespace !== CHAIN_NAMESPACES.EIP155) return false;
    if (eipStandard === EIP_STANDARD.EIP_7702) return Boolean(network.isInfuraSupported);
    return true;
  });
};

/**
 * Build the `chains` array for a SmartAccountsConfiguration payload.
 *
 * EIP-4337 requires a bundler URL per chain, so only chains that have one
 * configured are included.  EIP-7702 does not use bundlers, so every
 * enabled chain is included regardless.
 */
export const buildSmartAccountChains = (
  networks: ReadonlyArray<{ chainId: string; bundlerUrl?: string; paymasterUrl?: string }>,
  eipStandard: EipStandard
): SmartAccountChainConfigItem[] => {
  const isEip7702 = eipStandard === EIP_STANDARD.EIP_7702;
  return networks
    .filter((n) => isEip7702 || Boolean(n.bundlerUrl))
    .map((n) => ({
      chainId: n.chainId,
      ...(n.bundlerUrl && { bundlerConfig: { url: n.bundlerUrl } }),
      ...(n.paymasterUrl && { paymasterConfig: { url: n.paymasterUrl } }),
    }));
};
